var apiService = require('./api-service');
var apigClientFactory = require('aws-api-gateway-client').default;

export default class PeopleData {

  static async getData(accessToken) {
    try {
      // Validate access token
      if (!accessToken || typeof accessToken !== 'string') {
        throw new Error('Missing or invalid access token');
      }
  
      // Set a timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch('/api/sociogram/list/persons/', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest', // Helps prevent CSRF
        },
        credentials: 'same-origin', // Only send cookies for same-origin requests
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      
      if (!data) {
        return null;
      }
      
      return PeopleData.sortByKey(data, "surname");
      
    } catch (error) {
      console.error('Error fetching person data:', error.message);
      throw new Error('Failed to fetch person data');
    }
  }

  static async getPerson(id, accessToken) {
    try {
      // Validate inputs
      if (!id || typeof id !== 'string' || !id.trim()) {
        throw new Error('Invalid ID parameter');
      }
      
      if (!accessToken || typeof accessToken !== 'string') {
        throw new Error('Missing or invalid access token');
      }
  
      // Sanitize the ID parameter to prevent path traversal
      const sanitizedId = encodeURIComponent(id.trim());
      
      // Set a timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`/api/sociogram/person/${sanitizedId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest', // Helps prevent CSRF
        },
        credentials: 'same-origin', // Only send cookies for same-origin requests
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      return data;
      
    } catch (error) {
      console.error('Error fetching person data:', error.message);
      throw new Error('Failed to fetch person data');
    }
  }

  static async queryData(people, accessToken) {
    try {
      // Validate inputs
      if (!people || !Array.isArray(people)) {
        throw new Error('Invalid people parameter: must be an array');
      }
      
      if (!accessToken || typeof accessToken !== 'string') {
        throw new Error('Missing or invalid access token');
      }
  
      // Set a timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch('/api/sociogram/query/persons', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest', // Helps prevent CSRF
        },
        credentials: 'same-origin', // Only send cookies for same-origin requests
        body: JSON.stringify({ "people": people }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      
      // Transform the data the same way as the original method
      const peopleArray = [];
      Object.keys(data).forEach(function(k) {
        peopleArray.push(data[k]);
      });
      
      return PeopleData.sortByKey(peopleArray, "surname");
      
    } catch (error) {
      console.error('Error querying person data:', error.message);
      throw new Error('Failed to query person data');
    }
  }

  static async deletePerson(id, accessToken) {
    try {
      // Validate inputs
      if (!id || typeof id !== 'string' || !id.trim()) {
        throw new Error('Invalid ID parameter');
      }
      
      if (!accessToken || typeof accessToken !== 'string') {
        throw new Error('Missing or invalid access token');
      }
  
      // Sanitize the ID parameter to prevent path traversal
      const sanitizedId = encodeURIComponent(id.trim());
      
      // Set a timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`/api/sociogram/persons/${sanitizedId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest', // Helps prevent CSRF
        },
        credentials: 'same-origin', // Only send cookies for same-origin requests
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }
      
      // Return success with no data, matching original behavior
      return null;
      
    } catch (error) {
      console.error('Error deleting person:', error.message);
      throw new Error('Failed to delete user');
    }
  }

  static async createPerson(formData, accessToken) {
    try {
      // Validate inputs
      if (!formData || typeof formData !== 'object') {
        throw new Error('Invalid formData parameter');
      }
      
      if (!accessToken || typeof accessToken !== 'string') {
        throw new Error('Missing or invalid access token');
      }
  
      // Set a timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch('/api/sociogram/persons/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest', // Helps prevent CSRF
        },
        credentials: 'same-origin', // Only send cookies for same-origin requests
        body: JSON.stringify(formData),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      return data;
      
    } catch (error) {
      console.error('Error creating person:', error.message);
      throw new Error('Failed to create person');
    }
  }

  static async updatePerson(id, formData, accessToken) {
    try {

      // Validate inputs
      if (!id || typeof id !== 'string' || !id.trim()) {
        throw new Error('Invalid ID parameter');
      }

      // Validate inputs
      if (!formData || typeof formData !== 'object') {
        throw new Error('Invalid formData parameter');
      }
      
      if (!accessToken || typeof accessToken !== 'string') {
        throw new Error('Missing or invalid access token');
      }

      // Sanitize the ID parameter to prevent path traversal
      const sanitizedId = encodeURIComponent(id.trim());
  
      // Set a timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`/api/sociogram/person/${sanitizedId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest', // Helps prevent CSRF
        },
        credentials: 'same-origin', // Only send cookies for same-origin requests
        body: JSON.stringify(formData),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }
      
      return;
      
    } catch (error) {
      console.error('Error updating person:', error.message);
      throw new Error('Failed to update person');
    }
  }

  static sortByKey(array, key) {
    return array.sort(function(a, b) {
        var x = a[key]; var y = b[key];
        return ((x < y) ? -1 : ((x > y) ? 1 : 0));
    });
  } 
}